import React, { useContext, useEffect, useState } from 'react';
import { OrderContext } from '../context/OrderContext';
import { AuthContext } from '../context/AuthContext';
import { CartContext } from '../context/CartContext';
import { getAllOrders } from '../services/orderService';
import { useNavigate } from 'react-router-dom';
import '../css/Orders.css';

const OrdersList = () => {
  const { orders, setOrders, deleteOrder, clearOldOrders, requestOrderEdit } = useContext(OrderContext);
  const { user } = useContext(AuthContext);
  const { setCurrentOrder, loadCart, duplicateOrderIntoCart } = useContext(CartContext);
  const navigate = useNavigate();

  const canDuplicate = !!user && (user.role === 'admin' || !!user.quoteEnabled);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const fetchedOrders = await getAllOrders(user.token);
        setOrders(fetchedOrders);
      } catch (error) {
        console.error('Errore durante il recupero degli ordini:', error);
      }
    };

    if (user && user.token) {
      fetchOrders();
    }
  }, [user, setOrders]);

  const formatPrice = (price) => Number(price || 0).toFixed(2).replace('.', ',');

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('it-IT');
  };

  const statusLabel = (status) => {
    const map = {
      preparato: 'preparato',
      inviato: 'inviato',
      ricevuto: 'ricevuto',
      rifiutato: 'rifiutato',
      edit_requested: 'richiesta modifica',
    };
    return map[status] || status || 'preparato';
  };

  const isAdmin = user?.role === 'admin';
  const canEditDirectly = (order) =>
    ['preparato', 'rifiutato'].includes(order?.status || 'preparato') ||
    (isAdmin && ['inviato', 'ricevuto'].includes(order?.status));
  // Il giro "richiedi modifica -> approvazione admin" serve solo a chi non può
  // già editare direttamente (l'admin modifica sempre in diretta, vedi sopra).
  const canAskEdit = (order) => !isAdmin && ['inviato', 'ricevuto'].includes(order?.status);

  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDeleteOrder = async (orderId) => {
    try {
      await deleteOrder(orderId, user.token);
    } catch (error) {
      console.error('Errore durante la cancellazione dell\'ordine:', error);
    }
  };

  const handleEditOrder = (order) => {
    setCurrentOrder(order);
    loadCart(order.items);
    navigate('/cart');
  };

  const handleDuplicateOrder = (order) => {
    duplicateOrderIntoCart(order);
    navigate('/cart');
  };

  const handleRequestEdit = async (order) => {
    const reason = window.prompt('Motivo della richiesta modifica ordine?');
    if (reason == null) return;
    const cleanReason = String(reason).trim();
    if (!cleanReason) {
      alert('Inserisci un motivo.');
      return;
    }

    try {
      await requestOrderEdit(order._id, cleanReason);
      alert('Richiesta modifica inviata all\'amministrazione.');
    } catch (error) {
      console.error('Errore durante la richiesta modifica:', error);
      alert('Impossibile inviare la richiesta modifica.');
    }
  };

  return (
    <div className="orders-list">
      <h2>Lista Ordini</h2>
      {orders.length === 0 ? (
        <p>Non ci sono ordini.</p>
      ) : (
        <>
          {isMobile ? (
            <div className="orders-cards">
              {orders.map((order) => (
                <div key={order.id || order._id} className="order-card">
                  <div className="order-line order-id">Ordine #{order.orderNumber || order.id || order._id}</div>
                  <div className="order-line">Riferimento: {order.reference || '-'}</div>
                  {order.recipient?.name || order.recipient?.company ? (
                    <div className="order-line">Destinatario: {order.recipient?.name || order.recipient?.company}</div>
                  ) : null}
                  <div className="order-line">Data: {formatDate(order.date)}</div>
                  <div className="order-line">Status: {statusLabel(order.status)}</div>
                  {order.rejectionReason ? <div className="order-line">Motivo rifiuto: {order.rejectionReason}</div> : null}
                  {order.editRequest?.status === 'pending' ? (
                    <div className="order-line">Richiesta modifica in attesa approvazione</div>
                  ) : null}
                  {order.revisedAfterSendAt ? (
                    <div className="order-line order-warning">⚠ Modificato dopo l'invio il {formatDate(order.revisedAfterSendAt)} — da rinviare al cliente</div>
                  ) : null}
                  <div className="order-line order-total-row">Totale: EUR {formatPrice(order.total || 0)}</div>
                  <div className="order-actions mobile-actions">
                    {canEditDirectly(order) ? (
                      <button className="order-btn primary" onClick={() => handleEditOrder(order)}>Modifica</button>
                    ) : null}
                    {canAskEdit(order) ? (
                      <button className="order-btn primary" onClick={() => handleRequestEdit(order)}>Richiedi modifica</button>
                    ) : null}
                    {canDuplicate ? (
                      <button className="order-btn" onClick={() => handleDuplicateOrder(order)}>Duplica</button>
                    ) : null}
                    <button className="order-btn danger" onClick={() => handleDeleteOrder(order.id || order._id)}>Cancella</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <ul className="orders-ul">
              {orders.map((order) => (
                <li key={order.id || order._id} className="order-li">
                  <div className="order-li-main">
                    <span className="order-id">Ordine #{order.orderNumber}</span>
                    <span className="order-ref">Riferimento: {order.reference || '-'}</span>
                    <span className="order-ref">Stato: {statusLabel(order.status)}</span>
                    <span className="order-total">Totale: EUR {formatPrice(order.total || 0)}</span>
                    {order.revisedAfterSendAt ? (
                      <span className="order-ref order-warning">⚠ Modificato dopo l'invio il {formatDate(order.revisedAfterSendAt)} — da rinviare</span>
                    ) : null}
                  </div>
                  <div className="order-actions">
                    {canEditDirectly(order) ? (
                      <button className="order-btn primary" onClick={() => handleEditOrder(order)}>Modifica</button>
                    ) : null}
                    {canAskEdit(order) ? (
                      <button className="order-btn primary" onClick={() => handleRequestEdit(order)}>Richiedi modifica</button>
                    ) : null}
                    {canDuplicate ? (
                      <button className="order-btn" onClick={() => handleDuplicateOrder(order)}>Duplica</button>
                    ) : null}
                    <button className="order-btn danger" onClick={() => handleDeleteOrder(order.id || order._id)}>Cancella</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <button onClick={clearOldOrders}>Pulisci Ordini Vecchi</button>
    </div>
  );
};

export default OrdersList;
